/** A figure split the way it is displayed: 7,234 players shows as 7.2 with "k+". */
export interface StatParts {
    n: number;
    decimals: number;
    suffix: string;
}

/**
 * The live numbers are shown exactly, no rounding and no "+": a precise 2,050 reads as a real
 * reading taken just now, which is the whole point of showing activity rather than totals.
 */
export function exactParts(value: number): StatParts {
    return {n: value, decimals: 0, suffix: ''};
}

/**
 * The cumulative stats stay rounded down — nobody needs the exact play count:
 * 236 → 230+, 7,234 → 7.2k+, 73,532 → 73k+, 1,123,494 → 1.1M+.
 */
export function roundedParts(value: number): StatParts {
    if (value >= 1_000_000) {
        return withTenths(Math.floor(value / 100_000) / 10, 'M+');
    }
    if (value >= 10_000) {
        return {n: Math.floor(value / 1_000), decimals: 0, suffix: 'k+'};
    }
    if (value >= 1_000) {
        return withTenths(Math.floor(value / 100) / 10, 'k+');
    }
    // Under 1000: round down to 2 significant digits
    if (value >= 100) {
        const factor = Math.pow(10, Math.floor(Math.log10(value)) - 1);
        return {n: Math.floor(value / factor) * factor, decimals: 0, suffix: '+'};
    }
    return {n: value, decimals: 0, suffix: '+'};
}

/** One decimal while it says something (7.2k+), none when it would only be a zero (7k+). */
function withTenths(n: number, suffix: string): StatParts {
    return {n, decimals: Number.isInteger(n) ? 0 : 1, suffix};
}
